/**
 * Touch controls v3 (D-055) — ONE scheme, direct and calm. Replaces the v2
 * dual-layout design after live playtesting ("more direct, not so fast"):
 *
 *  - LEFT half = pseudo-joystick: where the thumb LANDS is neutral; steer
 *    strength is proportional to horizontal offset from that anchor (full
 *    deflection at TOUCH_JOY_FULL_PX), quantized to 1/100 exactly like the
 *    gamepad stick, so replays stay bit-identical. Lift = neutral.
 *    Small tilts = slow precise drift; big offset = full-speed dodge.
 *
 *  - RIGHT half = jump ON TOUCHDOWN, not on release — a rhythm game cannot
 *    afford tap-release latency. No hold semantics, no swipe-jump (the old
 *    swipe detector could misfire from a drifting steer thumb), and holding
 *    a finger on the right never changes the throttle anymore.
 *
 *  - RIGHT EDGE = throttle slider with a detent notch at 1.0; the grab zone
 *    is deliberately BROAD (TOUCH_SLIDER_*) so the thumb finds it blind.
 *    The slider produces a TARGET; frame() converts it to digital
 *    accel/brake against the sim's current throttle (the input log stays
 *    boolean — R2 §10 forbids analog throttle in the log).
 */
import {
  THROTTLE_MIN,
  THROTTLE_MAX,
  THROTTLE_DETENT_BAND,
  TOUCH_JOY_FULL_PX,
  TOUCH_SLIDER_FRAC,
  TOUCH_SLIDER_MIN_PX,
  TOUCH_SLIDER_MAX_PX,
} from "../config/tuning";
import type { InputFrame } from "./sim";

const JUMP_PULSE_FRAMES = 3;

/** The minimal event shape the handlers need (testable without PointerEvent). */
interface PointerLike {
  pointerId: number;
  pointerType: string;
  clientX: number;
  clientY: number;
}

/** Viewport indirection so tests can inject a fake size. */
export interface Viewport {
  width(): number;
  height(): number;
}

const windowViewport: Viewport = {
  width: () => window.innerWidth,
  height: () => window.innerHeight,
};

type TouchKind = "joy" | "slider" | "jump";

interface ActiveTouch {
  kind: TouchKind;
  anchorX: number;
  anchorY: number;
  x: number;
  y: number;
}

export interface JoyState {
  anchorX: number;
  anchorY: number;
  /** Signed deflection −1..1 (already clamped, unquantized for visuals). */
  dx: number;
}

export class TouchInput {
  private touches = new Map<number, ActiveTouch>();
  private jumpPulse = 0;
  private sawTouch = false;
  /** The target throttle the slider thumb is holding (null = resting). */
  sliderTarget: number | null = null;

  constructor(private readonly viewport: Viewport = windowViewport) {}

  attach(target: Pick<Window, "addEventListener">): void {
    // PointerEvent satisfies PointerLike; the narrower type keeps the
    // handlers testable without a DOM
    target.addEventListener("pointerdown", this.onDown as unknown as EventListener);
    target.addEventListener("pointermove", this.onMove as unknown as EventListener);
    target.addEventListener("pointerup", this.onUp as unknown as EventListener);
    target.addEventListener("pointercancel", this.onUp as unknown as EventListener);
  }

  private sliderZonePx(): number {
    return Math.max(
      TOUCH_SLIDER_MIN_PX,
      Math.min(TOUCH_SLIDER_MAX_PX, this.viewport.width() * TOUCH_SLIDER_FRAC),
    );
  }

  private sliderValue(y: number): number {
    const frac = 1 - Math.max(0, Math.min(1, y / this.viewport.height()));
    let v = THROTTLE_MIN + frac * (THROTTLE_MAX - THROTTLE_MIN);
    if (Math.abs(v - 1) <= THROTTLE_DETENT_BAND * 3) v = 1; // detent notch
    return v;
  }

  private onDown = (e: PointerLike): void => {
    if (e.pointerType !== "touch") return;
    this.sawTouch = true;
    const w = this.viewport.width();
    let kind: TouchKind;
    if (e.clientX > w - this.sliderZonePx()) {
      kind = "slider";
      this.sliderTarget = this.sliderValue(e.clientY);
    } else if (e.clientX < w / 2) {
      kind = "joy";
    } else {
      kind = "jump";
      this.jumpPulse = JUMP_PULSE_FRAMES; // on TOUCHDOWN — zero added latency
    }
    this.touches.set(e.pointerId, {
      kind,
      anchorX: e.clientX,
      anchorY: e.clientY,
      x: e.clientX,
      y: e.clientY,
    });
  };

  private onMove = (e: PointerLike): void => {
    const t = this.touches.get(e.pointerId);
    if (!t) return;
    t.x = e.clientX;
    t.y = e.clientY;
    if (t.kind === "slider") this.sliderTarget = this.sliderValue(e.clientY);
  };

  private onUp = (e: PointerLike): void => {
    const t = this.touches.get(e.pointerId);
    this.touches.delete(e.pointerId);
    if (!t) return;
    if (t.kind === "slider") {
      // thumb lifted — hold current throttle (no target to chase)
      if (![...this.touches.values()].some((o) => o.kind === "slider")) {
        this.sliderTarget = null;
      }
    }
  };

  get isTouchDevice(): boolean {
    return this.sawTouch;
  }

  /** Live joystick state for the on-screen indicator (null when idle). */
  joyState(): JoyState | null {
    let latest: ActiveTouch | null = null;
    for (const t of this.touches.values()) {
      if (t.kind === "joy") latest = t; // Map preserves insertion order
    }
    if (!latest) return null;
    const dx = Math.max(-1, Math.min(1, (latest.x - latest.anchorX) / TOUCH_JOY_FULL_PX));
    return { anchorX: latest.anchorX, anchorY: latest.anchorY, dx };
  }

  /**
   * Current frame. `simThrottle` lets the slider emit digital accel/brake
   * toward its target. Steer is quantized to 1/100 (gamepad precedent) so
   * the input log replays exactly.
   */
  frame(simThrottle: number): InputFrame {
    const joy = this.joyState();
    const steer = joy ? Math.round(joy.dx * 100) / 100 : 0;
    let accel = false;
    let brake = false;
    if (this.sliderTarget !== null) {
      if (this.sliderTarget > simThrottle + THROTTLE_DETENT_BAND) accel = true;
      else if (this.sliderTarget < simThrottle - THROTTLE_DETENT_BAND) brake = true;
    }
    const jumpHeld = this.jumpPulse > 0;
    if (this.jumpPulse > 0) this.jumpPulse--;
    return { steer, jumpHeld, accel, brake };
  }
}
